import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth, type UdisysUser } from '@/hooks/useAuth';
import { apiClient } from '@/lib/apiClient';

interface PermissaoItem {
  formularioId: number;
  podeLer:      boolean;
  podeIncluir:  boolean;
  podeEditar:   boolean;
  podeExcluir:  boolean;
}

interface PermissoesContextType {
  permissoes:   PermissaoItem[];
  carregando:   boolean;
  temPermissao: (formularioId: number, acao: 'ler' | 'incluir' | 'editar' | 'excluir') => boolean;
  recarregar:   () => Promise<void>;
  /** Recarrega SEM alternar "carregando" (nada desmonta enquanto busca).
   *  Falhou? Mantém as permissões atuais. */
  revalidar:    () => Promise<void>;
}

const PermissoesContext = createContext<PermissoesContextType | null>(null);

export function PermissoesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [permissoes, setPermissoes] = useState<PermissaoItem[]>([]);
  // Id do usuário cujas permissões já estão carregadas (null = nenhum/logout).
  const [permUserId, setPermUserId] = useState<number | null>(null);
  const [buscando, setBuscando] = useState(false);

  const carregar = useCallback(async (u: UdisysUser) => {
    setBuscando(true);
    try {
      const { data } = await apiClient.get('/api/auth/permissoes');
      setPermissoes(data.formularios ?? []);
    } catch {
      setPermissoes([]);
    } finally {
      setBuscando(false);
      setPermUserId(u.id);
    }
  }, []);

  useEffect(() => {
    if (user) {
      carregar(user);
    } else {
      setPermissoes([]);
      setPermUserId(null);
    }
  }, [user, carregar]);

  // Revalidação SILENCIOSA: atualiza as permissões sem alternar "carregando".
  // Importante porque o ProtectedRoute externo (layout) troca a árvore inteira
  // pelo spinner quando "carregando" vira true — o que desmontaria quem chamou.
  const revalidar = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await apiClient.get('/api/auth/permissoes');
      setPermissoes(data.formularios ?? []);
    } catch {
      // falhou (offline etc.) — mantém as permissões atuais
    }
  }, [user]);

  // Revalida ao voltar o foco para a janela/aba: permissões concedidas ou
  // revogadas em /config/perfis passam a valer sem relogin/F5.
  useEffect(() => {
    if (!user) return;
    let ultimaRevalidacao = Date.now(); // o mount já carregou; throttle desde já
    const aoFocar = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimaRevalidacao < 30_000) return;
      ultimaRevalidacao = Date.now();
      void revalidar();
    };
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);
    return () => {
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
    };
  }, [user, revalidar]);

  // "carregando" é DERIVADO: verdadeiro enquanto houver um usuário cujas
  // permissões ainda não foram carregadas (permUserId != user.id) ou enquanto a
  // busca está em andamento. Isso atualiza de forma síncrona com a troca de
  // usuário — sem a janela em que o ProtectedRoute avaliava permissões vazias
  // logo após o login (full load / refresh em rota protegida).
  const carregando = buscando || (!!user && permUserId !== user.id);

  const recarregar = useCallback(async () => {
    if (user) await carregar(user);
  }, [user, carregar]);

  const temPermissao = useCallback(
    (formularioId: number, acao: 'ler' | 'incluir' | 'editar' | 'excluir'): boolean => {
      if (user?.isAdminGlobal) return true;
      const p = permissoes.find(x => x.formularioId === formularioId);
      if (!p) return false;
      return { ler: p.podeLer, incluir: p.podeIncluir, editar: p.podeEditar, excluir: p.podeExcluir }[acao];
    },
    [permissoes, user]
  );

  return (
    <PermissoesContext.Provider value={{ permissoes, carregando, temPermissao, recarregar, revalidar }}>
      {children}
    </PermissoesContext.Provider>
  );
}

export function usePermissoes() {
  const ctx = useContext(PermissoesContext);
  if (!ctx) throw new Error('usePermissoes deve ser usado dentro de PermissoesProvider');
  return ctx;
}
